import { useTranslation } from 'react-i18next';
import { useState } from 'react'
import * as d3 from 'd3';
import { useD3 } from 'hooks';
import './index.scss';
import { numberWithCommas, extractMonth, formatNumber } from 'utils';
import Legend from 'components/emotion/Legend';
import { theme } from 'styles@emotion/theme';

export default function OverviewNewLeadToCustomerChart({
  title,
  chartId,
  data,
  svgWidth,
  svgHeight,
}) {
  const { t } = useTranslation();
  const [ drawFunction, setDrawFunction ] = useState(() => {})
  const categories = ['LTC', 'newLead', 'newCustomer'];

  function arc(r, sign) {
    return r ? `a${r * sign[0]},${r * sign[1]} 0 0 1 ${r * sign[2]},${r * sign[3]}` : '';
  }
  function roundedRect(x, y, width, height, r) {
    r = [
      Math.min(r[0], height, width),
      Math.min(r[1], height, width),
      Math.min(r[2], height, width),
      Math.min(r[3], height, width),
    ];

    return `M${x + r[0]},${y}h${width - r[0] - r[1]}${arc(r[1], [1, 1, 1, 1])}v${
      height - r[1] - r[2]
    }${arc(r[2], [1, 1, -1, 1])}h${-width + r[2] + r[3]}${arc(r[3], [1, 1, -1, -1])}v${
      -height + r[3] + r[0]
    }${arc(r[0], [1, 1, 1, -1])}z`;
  }
  const ref = useD3(
    (svg) => {
      document.getElementsByClassName(`overview-nltc-chart ${chartId}`)[0].innerHTML = '';
      const prevTooltip = document.getElementsByClassName(`tooltip ${chartId}`)[0];
      if (prevTooltip) prevTooltip.remove();
      svg.attr('width', svgWidth).attr('height', svgHeight);

      const margin = { top: 20, right: 30, bottom: 20, left: 45 };
      const chartWidth = svgWidth,
        chartHeight = svgHeight - 30 - 30; // legend, year indicators

      const xAxis = svg.append('g').attr('class', `x-axis ${chartId}`);
      const yLeftAxis = svg.append('g').attr('class', `y-left-axis ${chartId}`);
      const yRightAxis = svg.append('g').attr('class', `y-right-axis ${chartId}`);
      const grid = svg.append('g').attr('class', `y-grid ${chartId}`);
      const bars = svg.append('g').attr('class', `bar-group ${chartId}`);
      const line = svg.append('g').attr('class', `line ${chartId}`);
      const invisibleLine = svg.append('g').attr('class', `line ${chartId}`);
      const tooltip = d3
        .select(`.overview-nltc-chart-container.${chartId}`)
        .append('div')
        .attr('class', `tooltip ${chartId}`)
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .call((div) => div.append('div').attr('class', 'x'))
        .call((div) =>
          div
            .append('div')
            .attr('class', 'y')
            .call((div) => div.append('span').attr('class', 'label'))
            .call((div) => div.append('span').attr('class', 'value'))
        );
      const guideCircle = svg
        .append('g')
        .attr('class', `tooltip-circle ${chartId}`)
        .append('circle')
        .style('fill', '#A12AFF')
        .attr('stroke', '#A12AFF')
        .attr('r', 5)
        .style('visibility', 'hidden');

      const yearIndicators = svg.append('g').attr('class', `year-indicators ${chartId}`);

      drawCharts(categories);

      function drawCharts(selectedCategories) {
        const isLtcDisabled = !selectedCategories.includes('LTC');
        const isNewLeadDisabled = !selectedCategories.includes('newLead');
        const isNewCustomerDisabled = !selectedCategories.includes('newCustomer');

        // data-related
        const barTotalD = data.newLead.reduce((acc, cur, i) => {
          const barD = {};
          barD.date = cur.x;
          if (!isNewLeadDisabled) barD.newLead = cur.y;
          if (!isNewCustomerDisabled) barD.newCustomer = data.newCustomer[i].y;
          acc.push(barD);
          return acc;
        }, []);
        const lineD = isLtcDisabled ? [] : data.ltc;
        const lineX = isLtcDisabled ? [] : d3.map(lineD, (v) => v.x);
        const lineY = isLtcDisabled ? [] : d3.map(lineD, (v) => v.y);

        const barTotalX = d3.map(barTotalD, (v) => v.date);
        const barGroupX = [];
        if (!isNewLeadDisabled) barGroupX.push('newLead');
        if (!isNewCustomerDisabled) barGroupX.push('newCustomer');

        //scales
        const xLineScale = d3
          .scaleBand()
          .domain(lineX)
          .range([margin.left, chartWidth - margin.right]);
        if (lineD.length !== 0) {
          xLineScale.invert = (() => {
            const domain = xLineScale.domain();
            const range = xLineScale.range();
            const invertedScale = d3.scaleQuantize().domain(range).range(domain);
            return (x) => invertedScale(x);
          })();
        }
        const yLineScale = d3
          .scaleLinear()
          .domain([0, d3.max(lineY.length > 0 ? [0, 1] : lineY)])
          .rangeRound([chartHeight, margin.top]);

        const xBarTotalScale = d3
          .scaleBand()
          .domain(barTotalX)
          .rangeRound([margin.left, chartWidth - margin.right])
          .paddingInner(0.2);
        const xBarGroupScale = d3
          .scaleBand()
          .domain(barGroupX)
          .rangeRound([0, xBarTotalScale.bandwidth()])
          .paddingInner(0.2);

        const barDMax =
          d3.max([0, d3.max(barTotalD, (d) => d3.max(barGroupX, (x) => d[x]))]) === 0
            ? 1
            : d3.max([0, d3.max(barTotalD, (d) => d3.max(barGroupX, (x) => d[x]))]);
        const yScale = d3
          .scaleLinear()
          .domain([0, barDMax])
          .rangeRound([chartHeight, margin.top])
          .nice();

        // x-axis
        xAxis
          .attr('transform', `translate(0, ${chartHeight})`)
          .call(
            d3
              .axisBottom(xBarTotalScale)
              .tickSizeOuter(0)
              .tickFormat((d) => extractMonth(d))
          )
          .select('.domain')
          .attr('stroke', '#DDDDDD');

        // y-axes
        yLeftAxis
          .attr('transform', `translate(${margin.left},0)`)
          .call(
            d3
              .axisLeft(yScale)
              .ticks(5)
              .tickFormat((v) => formatNumber(v))
          )
          .select('.domain')
          .remove();
        yRightAxis
          .attr('transform', `translate(${chartWidth - margin.right},0)`)
          .call(d3.axisRight(yLineScale).ticks(5))
          .select('.domain')
          .remove();

        // grid
        grid
          .attr('transform', `translate(${margin.left},0)`)
          .call(
            d3
              .axisLeft(yScale)
              .ticks(5, '~s')
              .tickSize(-(chartWidth - margin.right - margin.left))
              .tickFormat('')
          )
          .call((g) => g.select('path').attr('stroke', 'transparent'))
          .call((g) => g.selectAll('.tick line').attr('stroke', '#DDDDDD'));

        if (barDMax === 1) {
          yLeftAxis.selectAll('.tick').style('opacity', 0);
          grid.selectAll('.tick').style('opacity', 0);
        } else {
          yLeftAxis.selectAll('.tick').style('opacity', 1);
          grid.selectAll('.tick').style('opacity', 1);
        }

        // bars
        const barsUpdate = bars.selectAll('g').data(barTotalD);
        barsUpdate
          .join('g')
          .call((g) =>
            g
              .attr('transform', (d) => `translate(${xBarTotalScale(d.date)},0)`)
              .selectAll('path')
              .data((d) => barGroupX.map((key) => ({ x: key, y: d[key] })))
              .join('path')
              .attr('d', (d) =>
                roundedRect(
                  xBarGroupScale(d.x),
                  yScale(d.y),
                  xBarGroupScale.bandwidth(),
                  chartHeight - yScale(d.y),
                  [2, 2, 0, 0]
                )
              )
              .attr('fill', (d) => {
                return d.x === 'newLead' ? '#C1BEF7' : '#4B43FF';
              })
          )
          .on('mouseover', () => {
            tooltip.style('visibility', 'visible');
          })
          .on('mousemove', (event, d) => {
            const tooltipX = tooltip.select('.x');
            const tooltipY = tooltip.select('.y');
            const xTotalPosition = xBarTotalScale(d.date);
            const [x, y] = d3.pointer(event);

            tooltipX.html(d.date);
            if (!isNewLeadDisabled && !isNewCustomerDisabled) {
              tooltipY
                .select('.label')
                .html(xBarTotalScale.bandwidth() / 2 < x ? 'Customer:&nbsp;' : 'Lead:&nbsp;');
              tooltipY
                .select('.value')
                .html(
                  numberWithCommas(xBarTotalScale.bandwidth() / 2 < x ? d.newCustomer : d.newLead)
                );
            } else if (!isNewLeadDisabled) {
              tooltipY.select('.label').html('Lead:&nbsp;');
              tooltipY.select('.value').html(numberWithCommas(d.newLead));
            } else if (!isNewCustomerDisabled) {
              tooltipY.select('.label').html('Customer:&nbsp;');
              tooltipY.select('.value').html(numberWithCommas(d.newCustomer));
            }

            tooltip
              .style(
                'left',
                x -
                  d3.select('.tooltip.nltc').node().getBoundingClientRect().width / 2 +
                  xTotalPosition +
                  'px'
              )
              .style('top', y - 30 + 'px');
          })
          .on('mouseout', () => tooltip.style('visibility', 'hidden'));

        // line
        const lineUpdate = line.selectAll('path').data([lineD]);
        lineUpdate
          .join('path')
          .attr('stroke', '#A12AFF')
          .attr('fill', 'transparent')
          .attr('stroke-linecap', 'round')
          .attr('stroke-width', 3)
          .style('pointer-events', 'stroke')
          .attr(
            'd',
            d3
              .line()
              .x((d) => xLineScale(d.x) + chartWidth / lineD.length / 2)
              .y((d) => yLineScale(d.y))
              .curve(d3.curveMonotoneX)
          )
        
        const invisibleLineUpdate = invisibleLine.selectAll('path').data([lineD]);
        invisibleLineUpdate
          .join('path')
          .attr('stroke', '#00000000')
          .attr('fill', 'transparent')
          .attr('stroke-linecap', 'round')
          .attr('stroke-width', 30)
          .style('pointer-events', 'stroke')
          .attr(
            'd',
            d3
              .line()
              .x((d) => xLineScale(d.x) + chartWidth / lineD.length / 2)
              .y((d) => yLineScale(d.y))
              .curve(d3.curveMonotoneX)
          )
          .on('mouseover', () => {
            tooltip.style('visibility', 'visible');
            guideCircle.style('visibility', 'visible');
          })
          .on('mousemove', (event, d) => {
            const tooltipX = tooltip.select('.x');
            const tooltipY = tooltip.select('.y');
            const [x, y] = d3.pointer(event);
            const xValue = xLineScale.invert(x);
            const yValue = d.filter((v) => v.x === xValue)[0].y;

            tooltipX.html(xValue);
            tooltipY.select('.label').html('LTC:&nbsp;');
            tooltipY.select('.value').html(yValue);
            tooltip
              .style(
                'left',
                d.map((v) => v.x).indexOf(xValue) * xLineScale.step() +
                  xLineScale.bandwidth() / 2 +
                  margin.left -
                  d3.select('.tooltip.nltc').node().getBoundingClientRect().width / 2 +
                  'px'
              )
              .style('top', yLineScale(yValue) - 25 + 'px');
            guideCircle
              .transition()
              .duration(1)
              .attr(
                'cx',
                d.map((v) => v.x).indexOf(xValue) * xLineScale.step() +
                  xLineScale.bandwidth() / 2 +
                  margin.left
              )
              .attr('cy', yLineScale(yValue));
          })
          .on('mouseout', () => {
            tooltip.style('visibility', 'hidden');
            guideCircle.style('visibility', 'hidden');
          });


        const yearIndicatorsD = barTotalX.reduce((acc, cur, idx) => {
          const year = cur.slice(0, 4);
          const yearMatchedObj = acc.filter((e) => e.year === year);
          if (yearMatchedObj.length === 0) {
            acc.push({
              year: year,
              firstIdx: idx,
              lastIdx: idx,
            });
          }
          acc.forEach((e) => {
            if (e.year === year) {
              e.lastIdx = idx;
              return;
            }
          });
          return acc;
        }, []);
        yearIndicatorsD.forEach((v, i) => {
          yearIndicators
            .append('text')
            .attr('class', 'year-indicator')
            .text(v.year)
            .attr(
              'x',
              margin.left + xBarTotalScale.step() * v.firstIdx + xBarTotalScale.bandwidth() / 5
            )
            .attr('y', chartHeight + margin.top + 20);
        });
      }
      setDrawFunction(() => {
        return drawCharts // if simply the function is passed, setState calls the function
      })
    },
    [data, svgWidth, svgHeight]
  );

  return (
    <div className={`overview-nltc-chart-container ${chartId}`}>
      <div className={`overview-nltc-chart-title ${chartId}`}>{title}</div>
      <Legend
        labels={[t("overview.ltc"), t("overview.lead"), t("overview.new_customer")]}
        values={['LTC', 'newLead', 'newCustomer']}
        colors={[theme.color.neonPurple500, theme.color.neonBlue100, theme.color.neonBlue500]}
        types={['line', 'circle', 'circle']}
        drawFunction={drawFunction} />
      <svg className={`overview-nltc-chart ${chartId}`} ref={ref}></svg>
    </div>
  );
}
